// ponytail: a 120-line schema editor (property table ⇄ JSON textarea) instead of
// porting rakerone-web's 2,000-line Extend schema-builder (dnd-kit, Monaco, two
// i18n namespaces, data-objects badge table). Upgrade path: copy that builder
// when nested objects/enums need visual editing here.
import { Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'

type Schema = Record<string, unknown>
type Prop = { type?: string | string[]; description?: string; items?: Schema; [k: string]: unknown }
const TYPES = ['string', 'number', 'integer', 'boolean', 'array', 'object'] as const

const typeOf = (p: Prop) =>
  Array.isArray(p.type) ? (p.type.find((t) => t !== 'null') ?? 'string') : (p.type ?? 'string')

export function SchemaEditor({
  value,
  onChange,
}: {
  value: Schema
  onChange: (next: Schema) => void
}) {
  const [tab, setTab] = useState<'form' | 'json'>('form')
  const [text, setText] = useState(() => JSON.stringify(value, null, 2))
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setText(JSON.stringify(value, null, 2)), [value])

  const props = (value.properties ?? {}) as Record<string, Prop>
  const required = (value.required ?? []) as string[]
  const update = (next: Partial<Schema>) => onChange({ ...value, ...next })
  const setProp = (key: string, p: Prop) => update({ properties: { ...props, [key]: p } })
  const rename = (from: string, to: string) => {
    if (!to || (to !== from && to in props)) return
    const entries = Object.entries(props).map(([k, v]) => [k === from ? to : k, v])
    update({
      properties: Object.fromEntries(entries),
      required: required.map((r) => (r === from ? to : r)),
    })
  }
  const remove = (key: string) => {
    const { [key]: _, ...rest } = props
    update({ properties: rest, required: required.filter((r) => r !== key) })
  }

  return (
    <div className="flex flex-col gap-2">
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'form' | 'json')}>
        <TabsList>
          <TabsTrigger value="form">Fields</TabsTrigger>
          <TabsTrigger value="json">JSON Schema</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === 'form' ? (
        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-[1fr_6rem_1.5fr_2rem_1.5rem] gap-1 px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            <span>Key</span>
            <span>Type</span>
            <span>Description</span>
            <span className="text-center">Req</span>
            <span />
          </div>
          {Object.entries(props).map(([key, p]) => (
            <div
              key={key}
              className="grid grid-cols-[1fr_6rem_1.5fr_2rem_1.5rem] items-center gap-1"
            >
              <Input
                aria-label="Field key"
                defaultValue={key}
                onBlur={(e) => rename(key, e.target.value.trim())}
                className="h-7 font-mono text-xs"
              />
              <select
                aria-label="Field type"
                value={typeOf(p)}
                onChange={(e) =>
                  setProp(key, {
                    ...p,
                    type: [e.target.value, 'null'],
                    ...(e.target.value === 'array' ? { items: p.items ?? { type: 'string' } } : {}),
                  })
                }
                className="h-7 rounded-md border border-input bg-background px-1 text-xs"
              >
                {TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <Input
                aria-label="Description"
                value={p.description ?? ''}
                onChange={(e) => setProp(key, { ...p, description: e.target.value })}
                className="h-7 text-xs"
              />
              <Checkbox
                aria-label="Required"
                checked={required.includes(key)}
                onCheckedChange={(c) =>
                  update({ required: c ? [...required, key] : required.filter((r) => r !== key) })
                }
                className="justify-self-center"
              />
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${key}`}
                onClick={() => remove(key)}
              >
                <Trash2 />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() =>
              setProp(`field_${Object.keys(props).length + 1}`, {
                type: ['string', 'null'],
                description: '',
              })
            }
          >
            Add field
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <Textarea
            aria-label="JSON Schema"
            value={text}
            spellCheck={false}
            className="min-h-64 font-mono text-xs"
            onChange={(e) => {
              setText(e.target.value)
              try {
                const parsed = JSON.parse(e.target.value)
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
                  throw new Error('root must be an object')
                setError(null)
                onChange(parsed)
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Invalid JSON')
              }
            }}
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
      )}
    </div>
  )
}
